import React, { useEffect, useState, useMemo } from "react";
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Input,
  Spinner,
  Row,
  Col,
  Label,
  Table
} from "reactstrap";
import Flatpickr from "react-flatpickr";
import { postDataAPI } from "../../helpers/phpbackend_helper";
import { useSelector } from "react-redux";

const targetDurationOptions = ["1 day", "3 days", "7 days", "14 days"];
const specialTargetDuration = "0 days";
const increasePeriodOptions = ["+10%", "+15%", "+20%", "+25%", "+50%"];

const AssignServersModal = ({ isOpen, toggle, card, isScenario = false }) => {
  const { currentOutlet } = useSelector((state) => ({
    currentOutlet: state.UserOutletGroup.currentOutlet
  }));
  const currency = currentOutlet?.currency || "AED";

  const [servers, setServers] = useState([]);
  const [selected, setSelected] = useState([]);
  const [initialSelected, setInitialSelected] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [completionDate, setCompletionDate] = useState(null);
  const [targetDuration, setTargetDuration] = useState("14 days");
  const [increasePeriod, setIncreasePeriod] = useState("+20%");
  const [isSpecialOfTheDay, setIsSpecialOfTheDay] = useState(false);
  const [isNoTarget, setIsNoTarget] = useState(false);
  const [step, setStep] = useState(1);
  const [loadingImpact, setLoadingImpact] = useState(false);
  const [impactData, setImpactData] = useState([]);

  const today = useMemo(() => {
    const date = new Date();
    date.setHours(0, 0, 0, 0);
    return date;
  }, []);

  const tomorrow = useMemo(() => {
    const date = new Date();
    date.setHours(0, 0, 0, 0);
    date.setDate(date.getDate() + 1);
    return date;
  }, []);

  const formatLocalDate = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const defaultDate = useMemo(() => {
    const date = new Date();
    date.setDate(date.getDate() + 7);
    return date;
  }, []);

  const capitalizeWords = (str) => {
    if (!str) return "";
    return str.split(" ").map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(" ");
  };

  const formatDateForDisplay = (date) => {
    if (!date) return "";
    const day = date.getDate();
    const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
    const month = months[date.getMonth()];
    const year = date.getFullYear();
    return `${day} ${month} ${year}`;
  };

  const targetDurationLabel = isSpecialOfTheDay ? "Today" : capitalizeWords(targetDuration);
  const baselinePeriodLabel = isSpecialOfTheDay ? "Previous Day" : capitalizeWords(targetDuration);

  // Only servers that are newly selected for assignment (not already assigned)
  const newlySelected = useMemo(
    () => selected.filter((id) => !initialSelected.includes(id)),
    [selected, initialSelected]
  );

  const newlySelectedServersData = useMemo(() => {
    return servers.filter((server) => newlySelected.includes(Number(server.server_id)));
  }, [servers, newlySelected]);

  const tableRows = useMemo(() => {
    let totalRev = 0;
    const pct = increasePeriod ? parseFloat(increasePeriod.replace(/[^\d.]/g, "")) : 0;

    const rows = newlySelectedServersData.map((server, index) => {
      const apiItem = Array.isArray(impactData)
        ? impactData.find(
            (item) => String(item.server_id) === String(server.server_id),
          )
        : null;

      const previousUnits = apiItem && apiItem.prev_qty !== undefined ? Number(apiItem.prev_qty) : 0;
      const targetUnits = previousUnits === 0 ? 1 : Math.round(previousUnits * (1 + pct / 100));
      const expectedLift = targetUnits - previousUnits;
      const expectedRevenueLift = apiItem && apiItem.expected_revenue !== undefined
        ? expectedLift === 0
          ? Number(apiItem.expected_revenue) || 0
          : (Number(apiItem.expected_revenue) || 0) * expectedLift
        : 0;

      totalRev += expectedRevenueLift;

      return {
        index: index + 1,
        name: server.name,
        previousUnits,
        targetUnits,
        expectedLift,
        expectedRevenueLift
      };
    });
    return { rows, totalRev };
  }, [newlySelectedServersData, impactData, increasePeriod]);

  const handleSpecialOfTheDayChange = (checked) => {
    setIsSpecialOfTheDay(checked);
    if (checked) {
      setIsNoTarget(false);
      setCompletionDate(today);
      setTargetDuration(specialTargetDuration);
      setIncreasePeriod("+20%");
    } else {
      setCompletionDate(defaultDate);
      setTargetDuration("14 days");
    }
  };

  const handleNoTargetChange = (checked) => {
    setIsNoTarget(checked);
    if (checked) {
      setIsSpecialOfTheDay(false);
      setCompletionDate(defaultDate);
      setTargetDuration("14 days");
      setIncreasePeriod("+20%");
    }
  };

  const handleSelectAll = (checked) => {
    if (checked) {
      const allServerIds = servers.map((s) => Number(s.server_id));
      setSelected(allServerIds);
    } else {
      setSelected([]);
    }
  };

  useEffect(() => {
    if (isOpen && card?.flash_id) {
      loadData();
      setCompletionDate(defaultDate);
      setTargetDuration("14 days");
      setIncreasePeriod("+20%");
      setIsSpecialOfTheDay(false);
      setIsNoTarget(false);
      setStep(1);
      setImpactData([]);
      setLoadingImpact(false);
    }
  }, [isOpen, card]);

  const loadData = async () => {
    setLoading(true);

    try {
      // 1️⃣ Load all servers
      const serverRes = await postDataAPI({
        serviceName: "server_data",
        op: "server_coaching_outlet_server_list",
        is_show_all: true,
        load: 0
      });

      // 2️⃣ Load assigned servers for flash card
      const assignedRes = await postDataAPI({
        serviceName: "server_data",
        op: "server_coaching_get_assign_server",
        flash_id: card.flash_id
      });

      if (serverRes.status === "ok") {
        setServers(serverRes.data.server_data || []);
      }

      if (assignedRes.status === "ok") {
        // console.log(assignedRes.data);
        const assignedIds = (assignedRes.data || []).map(Number);
        setSelected(assignedIds);
        setInitialSelected(assignedIds);
      }
    } finally {
      setLoading(false);
    }
  };

  const toggleSelect = (id) => {
    setSelected((prev) =>
      prev.includes(id)
        ? prev.filter((x) => x !== id)
        : [...prev, id]
    );
  };

  const handleSave = async () => {
    const hasNewAssignments = selected.some((id) => !initialSelected.includes(id));
    if (!isScenario && hasNewAssignments && !completionDate) {
      alert("Please select a completion date for the assigned servers");
      return;
    }

    setIsSaving(true);

    try {
      const payload = {
        serviceName: "server_data",
        op: "create_update_flash_card_servers",
        flash_card_id: card.flash_id,
        server_id: selected,
      };

      if (!isScenario && completionDate) {
        payload.target_complete_date = formatLocalDate(completionDate);
        payload.target_duration = isNoTarget
          ? null
          : isSpecialOfTheDay
            ? "0"
            : (targetDuration ? targetDuration.replace(/\D/g, "") : "");
        payload.increase_period = isNoTarget
          ? null
          : (increasePeriod ? increasePeriod.replace(/\D/g, "") : "");
      } else if (isScenario) {
        payload.target_complete_date = null;
        payload.target_duration = null;
        payload.increase_period = null;
      }

      await postDataAPI(payload);

      toggle();
    } finally {
      setIsSaving(false);
    }
  };

  const handleNextStep = () => {
    // Scenarios and No target: assign immediately (skip impact review)
    if (isScenario || isNoTarget) {
      handleSave();
      return;
    }

    // Impact review is only for newly assigning servers
    if (newlySelected.length === 0) {
      handleSave();
      return;
    }

    if (!completionDate) {
      alert("Please select a completion date for the assigned servers");
      return;
    }

    // Step 1 Next: only fetch impact review for new servers — assign/save happens on step 2
    setLoadingImpact(true);
    postDataAPI({
      serviceName: "server_data",
      op: "review-impact-flash-card-servers",
      flash_card_id: card?.flash_id,
      server_id: newlySelected,
      target_duration: isSpecialOfTheDay
        ? "0"
        : (targetDuration ? targetDuration.replace(/\D/g, "") : ""),
      target_percent: isNoTarget
          ? null
          : (increasePeriod ? increasePeriod.replace(/\D/g, "") : "")
    })
      .then((res) => {
        if (res.status === "ok") {
          setImpactData(res.data || []);
          setStep(2);
        } else {
          alert(res.message || "Failed to fetch review impact targets");
        }
      })
      .catch((err) => {
        console.error("Error fetching impact data:", err);
        alert("An error occurred while fetching impact data.");
      })
      .finally(() => setLoadingImpact(false));
  };

  const LoadingOverlay = ({ text }) => (
    <div
      style={{
        position: "absolute",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: "rgba(255,255,255,0.8)",
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
        zIndex: 9999,
      }}
    >
      <Spinner color="primary" />
      <p className="mt-2">{text}</p>
    </div>
  );

  return (
    <>
      <Modal isOpen={isOpen} toggle={toggle} centered size="xl">
        {(isSaving || loadingImpact) && (
          <LoadingOverlay
            text={loadingImpact ? "Loading impact review..." : "Saving changes..."}
          />
        )}
        
        <ModalHeader toggle={toggle}>
          {step === 2
            ? `Review Impact - ${card?.item_name || ""}`
            : `Assign Servers to ${card?.item_name || ""}`}
          <div className="text-muted small">
            {step === 2
              ? "Please review the targets and expected impact before assigning."
              : "Select servers to assign"}
          </div>
        </ModalHeader>

        <ModalBody style={step === 2 ? { maxHeight: "60vh", overflowY: "auto" } : {}}>
          {loading && <p className="text-muted">Loading...</p>}

          {/* Stepper */}
          {!loading && !isNoTarget && !isScenario && (
            <div className="d-flex align-items-center justify-content-start mb-4 px-2">
              {/* Step 1 */}
              <div className="d-flex align-items-center">
                {step > 1 ? (
                  <>
                    <div className="d-flex align-items-center justify-content-center border rounded-circle me-2" style={{ width: 24, height: 24, minWidth: 24, borderColor: "#405189" }}>
                      <i className="ri-check-line fw-bold" style={{ fontSize: 13, color: "#405189" }}></i>
                    </div>
                    <span className="small fw-semibold" style={{ color: "#405189" }}>Select Servers</span>
                  </>
                ) : (
                  <>
                    <div className="d-flex align-items-center justify-content-center text-white rounded-circle me-2" style={{ width: 24, height: 24, minWidth: 24, fontSize: 12, fontWeight: "bold", backgroundColor: "#405189" }}>
                      1
                    </div>
                    <span className="fw-bold small" style={{ color: "#405189" }}>Select Servers</span>
                  </>
                )}
              </div>
              
              {/* Divider Line */}
              <div className="mx-3" style={{ flex: "1 1 auto", minWidth: "15px", maxWidth: "60px", height: "2px", backgroundColor: step > 1 ? "#405189" : "#e2e8f0" }}></div>

              {/* Step 2 */}
              <div className="d-flex align-items-center">
                {step === 2 ? (
                  <>
                    <div className="d-flex align-items-center justify-content-center text-white rounded-circle me-2" style={{ width: 24, height: 24, minWidth: 24, fontSize: 12, fontWeight: "bold", backgroundColor: "#405189" }}>
                      2
                    </div>
                    <span className="fw-bold small" style={{ color: "#405189" }}>Review Impact</span>
                  </>
                ) : (
                  <>
                    <div className="d-flex align-items-center justify-content-center border rounded-circle me-2" style={{ width: 24, height: 24, minWidth: 24, fontSize: 12, fontWeight: "bold", borderColor: "#ced4da", color: "#6c757d" }}>
                      2
                    </div>
                    <span className="text-muted small" style={{ fontWeight: "400" }}>Review Impact</span>
                  </>
                )}
              </div>
            </div>
          )}

          {!loading && step === 2 ? (
            <>
              {/* Banner summary card */}
              <div className="d-flex flex-column flex-md-row align-items-stretch align-items-md-center p-3 mb-4 border rounded-3" style={{ backgroundColor: "rgba(64, 81, 137, 0.03)", borderColor: "rgba(64, 81, 137, 0.15)" }}>
                <div className="d-flex align-items-center mb-3 mb-md-0 flex-grow-1">
                  <div className="me-3 flex-shrink-0 d-flex align-items-center justify-content-center" style={{ width: 44, height: 44, minWidth: 44 }}>
                    <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="#405189" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <circle cx="12" cy="12" r="10" />
                      <circle cx="12" cy="12" r="6" />
                      <circle cx="12" cy="12" r="2" fill="#405189" />
                      <path d="M12 2v4M12 18v4M2 12h4M18 12h4" />
                    </svg>
                  </div>
                  <div style={{ minWidth: 0 }}>
                    <div className="fw-bold text-dark mb-1" style={{ fontSize: "13px", lineHeight: "1.4" }}>Targets will be created automatically when the quiz is completed.</div>
                    <div className="text-muted" style={{ fontSize: "11px" }}>
                      {isSpecialOfTheDay
                        ? "Performance will be measured for Today and compared to the Previous Day."
                        : `Performance will be measured for ${targetDurationLabel} and compared to the previous ${targetDurationLabel}.`}
                    </div>
                  </div>
                </div>
                <div className="d-flex flex-wrap flex-shrink-0 responsive-banner-divider" style={{ gap: "12px" }}>
                  <div className="responsive-col-item responsive-col-border">
                    <div className="text-muted text-nowrap" style={{ fontSize: "10px", fontWeight: "500", marginBottom: 4 }}>Target Duration</div>
                    <div className="fw-bold text-dark" style={{ fontSize: "14px" }}>{targetDurationLabel}</div>
                  </div>
                  <div className="responsive-col-item responsive-col-border">
                    <div className="text-muted text-nowrap" style={{ fontSize: "10px", fontWeight: "500", marginBottom: 4 }}>Increase Over Previous</div>
                    <div className="fw-bold text-dark" style={{ fontSize: "14px" }}>{increasePeriod}</div>
                  </div>
                  <div className="responsive-col-item">
                    <div className="text-muted text-nowrap" style={{ fontSize: "10px", fontWeight: "500", marginBottom: 4 }}>Complete By</div>
                    <div className="fw-bold text-dark" style={{ fontSize: "14px" }}>{formatDateForDisplay(completionDate)}</div>
                  </div>
                </div>
              </div>

              {/* Impact Summary Title */}
              <h6 className="fw-bold text-dark mb-3">Impact Summary</h6>

              {/* Dynamic Cards Grid */}
              <Row className="g-2 mb-4">
                <Col sm="6" md="3">
                  <div className="border rounded-3 p-3 d-flex align-items-center h-100 bg-white">
                    <div className="rounded-circle d-flex align-items-center justify-content-center flex-shrink-0 me-3" style={{ width: 48, height: 48, minWidth: 48, backgroundColor: "rgba(64, 81, 137, 0.08)", color: "#405189" }}>
                      <i className="ri-group-line" style={{ fontSize: 20 }}></i>
                    </div>
                    <div style={{ minWidth: 0 }}>
                      <div className="text-muted" style={{ fontSize: "12px", fontWeight: "500", marginBottom: 2 }}>Total Servers</div>
                      <div className="fw-bold text-dark" style={{ fontSize: "20px", lineHeight: 1.2 }}>{newlySelected.length}</div>
                    </div>
                  </div>
                </Col>
                <Col sm="6" md="3">
                  <div className="border rounded-3 p-3 d-flex align-items-center h-100 bg-white">
                    <div className="rounded-circle d-flex align-items-center justify-content-center flex-shrink-0 me-3" style={{ width: 48, height: 48, minWidth: 48, backgroundColor: "rgba(10, 191, 115, 0.08)", color: "#0abf73" }}>
                      <i className="ri-line-chart-line" style={{ fontSize: 20 }}></i>
                    </div>
                    <div style={{ minWidth: 0 }}>
                      <div className="text-muted" style={{ fontSize: "12px", fontWeight: "500", marginBottom: 2 }}>Expected Additional Revenue</div>
                      <div className="fw-bold text-dark" style={{ fontSize: "20px", lineHeight: 1.2 }}>{currency} {Number(tableRows.totalRev || 0).toFixed(2)}</div>
                      <div className="text-muted" style={{ fontSize: "11px" }}>Over {targetDurationLabel}</div>
                    </div>
                  </div>
                </Col>
                <Col sm="6" md="3">
                  <div className="border rounded-3 p-3 d-flex align-items-center h-100 bg-white">
                    <div className="rounded-circle d-flex align-items-center justify-content-center flex-shrink-0 me-3" style={{ width: 48, height: 48, minWidth: 48, backgroundColor: "rgba(147, 51, 234, 0.08)", color: "#9333ea" }}>
                      <i className="ri-percent-line" style={{ fontSize: 20 }}></i>
                    </div>
                    <div style={{ minWidth: 0 }}>
                      <div className="text-muted" style={{ fontSize: "12px", fontWeight: "500", marginBottom: 2 }}>Increase Over Previous</div>
                      <div className="fw-bold" style={{ fontSize: "20px", lineHeight: 1.2, color: "#9333ea" }}>{increasePeriod}</div>
                      <div className="text-muted" style={{ fontSize: "11px" }}>Performance Uplift</div>
                    </div>
                  </div>
                </Col>
                <Col sm="6" md="3">
                  <div className="border rounded-3 p-3 d-flex align-items-center h-100 bg-white">
                    <div className="rounded-circle d-flex align-items-center justify-content-center flex-shrink-0 me-3" style={{ width: 48, height: 48, minWidth: 48, backgroundColor: "rgba(247, 144, 9, 0.08)", color: "#f79009" }}>
                      <i className="ri-timer-line" style={{ fontSize: 20 }}></i>
                    </div>
                    <div style={{ minWidth: 0 }}>
                      <div className="text-muted" style={{ fontSize: "12px", fontWeight: "500", marginBottom: 2 }}>Baseline Period</div>
                      <div className="fw-bold text-dark" style={{ fontSize: "20px", lineHeight: 1.2 }}>{baselinePeriodLabel}</div>
                      <div className="text-muted" style={{ fontSize: "11px" }}>
                        {isSpecialOfTheDay ? "Previous Day" : `Previous ${baselinePeriodLabel}`}
                      </div>
                    </div>
                  </div>
                </Col>
              </Row>

              {/* Generated Targets Title */}
              <h6 className="fw-bold text-dark mb-3">Generated Targets</h6>

              {/* Table of Targets */}
              <div className="table-responsive border rounded-3 mb-3">
                <Table hover className="align-middle mb-0 bg-white" style={{ tableLayout: "fixed", width: "100%", minWidth: "750px" }}>
                  <colgroup>
                    <col style={{ width: "5%" }} />
                    <col style={{ width: "28%" }} />
                    <col style={{ width: "16%" }} />
                    <col style={{ width: "13%" }} />
                    <col style={{ width: "14%" }} />
                    <col style={{ width: "24%" }} />
                  </colgroup>
                  <thead className="table-light">
                    <tr style={{ fontSize: "11px", color: "#6c757d" }}>
                      <th className="ps-3 text-start fw-semibold">#</th>
                      <th className="text-start fw-semibold">Server</th>
                      <th className="text-center fw-semibold">
                        <div>{isSpecialOfTheDay ? "Previous Day" : `Previous ${baselinePeriodLabel}`}</div>
                        <div className="fw-normal" style={{ fontSize: "10px", textTransform: "none" }}>(Units Sold)</div>
                      </th>
                      <th className="text-center fw-semibold">
                        <div>Target</div>
                        <div className="fw-normal" style={{ fontSize: "10px", textTransform: "none" }}>({targetDurationLabel})</div>
                      </th>
                      <th className="text-center fw-semibold">
                        <div>Expected Lift</div>
                        <div className="fw-normal" style={{ fontSize: "10px", textTransform: "none" }}>(Units)</div>
                      </th>
                      <th className="text-center fw-semibold">
                        <div>Expected Revenue Lift</div>
                        <div className="fw-normal text-center" style={{ fontSize: "10px", textTransform: "none" }}>({currency})</div>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {tableRows.rows.map((row) => (
                      <tr key={row.index} style={{ fontSize: "13px" }}>
                        <td className="ps-3 text-start text-muted">{row.index}</td>
                        <td className="text-start fw-semibold text-dark">{row.name}</td>
                        <td className="text-center">{row.previousUnits}</td>
                        <td className="text-center">{row.targetUnits}</td>
                        <td className="text-center">{row.expectedLift}</td>
                        <td className="text-center pe-3 fw-bold" style={{ color: "#212529" }}>
                          {currency} {Number(row.expectedRevenueLift || 0).toFixed(2)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>

              {/* Expected Lift Green Message Box */}
              <div className="d-flex align-items-center p-3 mb-3 border rounded-3" style={{ backgroundColor: "rgba(10, 191, 115, 0.05)", borderColor: "rgba(10, 191, 115, 0.15)", color: "#0abf73" }}>
                <i className="ri-line-chart-line me-2 fs-5"></i>
                <span className="small fw-semibold">
                  This training is expected to generate an additional {currency} {Number(tableRows.totalRev || 0).toFixed(2)} in revenue over {isSpecialOfTheDay ? "Today" : `the next ${targetDurationLabel}`}.
                </span>
              </div>
            </>
          ) : (
            <>
              {/* Step 1 Content */}
              <div className="form-check d-flex align-items-center mb-3">
                <Input
                  type="checkbox"
                  checked={servers.length > 0 && selected.length === servers.length}
                  onChange={(e) => handleSelectAll(e.target.checked)}
                  className="form-check-input me-2 mt-0"
                  id="selectAllServers"
                />
                <Label className="form-check-label fw-bold mb-0" htmlFor="selectAllServers" style={{ cursor: "pointer" }}>
                  Select All Servers ({servers.length})
                </Label>
              </div>

              <div className="mb-4 px-1" style={{ maxHeight: "200px", overflowY: "auto" }}>
                {servers.map((server) => (
                  <div
                    key={server.server_id}
                    className="form-check d-flex align-items-center mb-2"
                  >
                    <Input
                      type="checkbox"
                      checked={selected.includes(Number(server.server_id))}
                      onChange={() => toggleSelect(Number(server.server_id))}
                      className="form-check-input me-2 mt-0"
                      id={`server-${server.server_id}`}
                    />
                    <Label
                      className="form-check-label mb-0"
                      htmlFor={`server-${server.server_id}`}
                      style={{ cursor: "pointer" }}
                    >
                      {server.name}
                    </Label>
                  </div>
                ))}
              </div>

              {!isScenario && (
                <>
              <hr className="my-3 text-muted" />
              <div className="mb-2">
                <div className="d-flex align-items-center mb-3">
                  <span className="fw-bold text-dark fs-5">Training Settings</span>
                  <span className="mx-2 text-muted">|</span>
                  <span className="text-muted small">Targets are automatically created when the quiz is completed.</span>
                </div>
                <Row className="g-3">
                  <Col md="4">
                    <Label className="text-muted small fw-bold">Complete Training By</Label>
                    <div className="position-relative d-flex align-items-center">
                      <i className="ri-calendar-todo-line position-absolute ms-3 text-muted" style={{ zIndex: 10, fontSize: 16 }}></i>
                      <Flatpickr
                        key={isSpecialOfTheDay ? "special-today" : "normal"}
                        className="form-control ps-5"
                        value={completionDate}
                        onChange={(dates) => setCompletionDate(dates[0] || null)}
                        options={{
                          dateFormat: "d M Y",
                          minDate: isSpecialOfTheDay ? today : tomorrow,
                          allowInput: !isSpecialOfTheDay,
                          clickOpens: !isSpecialOfTheDay
                        }}
                        disabled={isSpecialOfTheDay}
                        placeholder="Select Completion Date"
                      />
                    </div>
                    <div className="form-check mt-2">
                      <Input
                        type="checkbox"
                        className="form-check-input"
                        id="specialOfTheDay"
                        checked={isSpecialOfTheDay}
                        disabled={isNoTarget}
                        onChange={(e) => handleSpecialOfTheDayChange(e.target.checked)}
                      />
                      <Label className="form-check-label small" htmlFor="specialOfTheDay">
                        Specials of the day (today)
                      </Label>
                    </div>
                    <div className="form-check mt-1">
                      <Input
                        type="checkbox"
                        className="form-check-input"
                        id="noTarget"
                        checked={isNoTarget}
                        disabled={isSpecialOfTheDay}
                        onChange={(e) => handleNoTargetChange(e.target.checked)}
                      />
                      <Label className="form-check-label small" htmlFor="noTarget">
                        No target
                      </Label>
                    </div>
                  </Col>
                  {!isNoTarget && (
                    <>
                      <Col md="4">
                        <Label className="text-muted small fw-bold">Target Duration (after quiz completion)</Label>
                        <Input
                          type="select"
                          className="form-select"
                          value={targetDuration}
                          onChange={(e) => setTargetDuration(e.target.value)}
                          disabled={isSpecialOfTheDay}
                        >
                          {isSpecialOfTheDay && (
                            <option value={specialTargetDuration}>
                              Today (0 days)
                            </option>
                          )}
                          {targetDurationOptions.map((opt) => (
                            <option key={opt} value={opt}>
                              {opt}
                            </option>
                          ))}
                        </Input>
                      </Col>
                      <Col md="4">
                        <Label className="text-muted small fw-bold">Increase over Previous Period</Label>
                        <Input
                          type="select"
                          className="form-select"
                          value={increasePeriod}
                          onChange={(e) => setIncreasePeriod(e.target.value)}
                        >
                          {increasePeriodOptions.map((opt) => (
                            <option key={opt} value={opt}>
                              {opt}
                            </option>
                          ))}
                        </Input>
                      </Col>
                    </>
                  )}
                </Row>
              </div>
                </>
              )}
            </>
          )}
        </ModalBody>

        {step === 2 ? (
          <ModalFooter className="w-100 flex-column align-items-stretch border-top-0">
            <div className="d-flex flex-column flex-sm-row justify-content-between align-items-stretch align-items-sm-center w-100 mb-3 gap-2">
              <Button outline onClick={() => setStep(1)} className="d-flex align-items-center justify-content-center gap-1">
                <i className="ri-arrow-left-line"></i> Back
              </Button>
              <Button
                color="primary"
                onClick={handleSave}
                disabled={isSaving}
                className="d-flex align-items-center justify-content-center gap-1"
              >
                {isSaving ? "Saving..." : "Assign Servers & Targets"}
              </Button>
            </div>
            <div className="text-muted small d-flex align-items-start gap-1 pt-2 border-top" style={{ fontSize: "11px" }}>
              <i className="ri-shield-check-line text-success flex-shrink-0" style={{ fontSize: "14px", marginTop: "1px" }}></i>
              <span>
                Targets will be created automatically when the quiz is completed by the assigned servers.
              </span>
            </div>
          </ModalFooter>
        ) : (
          <ModalFooter className="w-100 justify-content-end">
            <div>
              <Button outline onClick={toggle} disabled={isSaving} className="me-2">
                Cancel
              </Button>

              <Button
                color="primary"
                onClick={handleNextStep}
                disabled={loading || isSaving || loadingImpact}
              >
                {isScenario || isNoTarget
                  ? (isSaving ? "Saving..." : "Save")
                  : (loadingImpact ? "Loading..." : "Next")}
              </Button>
            </div>
          </ModalFooter>
        )}
      </Modal>
    </>
  );
};

export default AssignServersModal;
